import React, { useState, useCallback, useRef } from 'react';
import useProductSizes from '../hooks/useProductSizes';
import ProductSizesTable from '../components/productSizes/productSizesTable';
import ProductSizeForm from '../components/productSizes/ProductSizeForm';
import ProductSizeActions from '../components/productSizes/ProductSizeActions';
import ProductSizeDetails from '../components/productSizes/ProductSizeDetails';
import ProductSizeImportModal from '../components/productSizes/ProductSizeImportModal';
import ProductSizeDistributionModal from '../components/productSizes/ProductSizeDistributionModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import MessageAlert from '../components/common/MessageAlert';
import { useErrorDialog } from '../context/errorDialogContext';
import productSizesService from '../services/productSizesServices';
import { AdminGuard } from '../components/RoleGuards';
import { useTranslation } from 'react-i18next';

/**
 * ProductSizesPage
 * - Provides UI to list, create, edit, view, delete, and bulk-delete product sizes.
 * - Uses the useProductSizes hook for data operations and state management.
 */
const ProductSizesPage = () => {
  const { t } = useTranslation();
  // Custom hook for product sizes data and operations
  const {
    productSizes,
    loading,
    error,
    totalCount,
    totalPages,
    fetchProductSizes,
    getProductSizeById,
    createProductSize,
    updateProductSize,
    deleteProductSize,
    bulkDeleteProductSizes,
    importProductSizes,
    exportProductSizes,
    clearError,
  } = useProductSizes();

  // Local state for UI interactions
  const [selectedProductSizes, setSelectedProductSizes] = useState([]);
  const [showProductSizeForm, setShowProductSizeForm] = useState(false);
  const [editingProductSize, setEditingProductSize] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [viewingProductSize, setViewingProductSize] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  // Active/inactive filter. Default shows only active sizes; deactivated
  // (soft-deleted) sizes are hidden until the operator switches this.
  const [statusFilter, setStatusFilter] = useState('active');
  const searchDebounceRef = useRef(null);
  const PAGE_LIMIT = 20;
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [distributionProductId, setDistributionProductId] = useState(null);

  const strings = t('productSizes', { returnObjects: true });
  const { showError } = useErrorDialog();

  const loadProductSizes = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshLoading(true);
    try {
      const skip = (currentPage - 1) * PAGE_LIMIT;
      // active -> only active (default); inactive -> only deactivated; all -> both.
      const statusParams =
        statusFilter === 'inactive' ? { is_active: false }
        : statusFilter === 'all' ? { include_inactive: true }
        : { is_active: true };
      await fetchProductSizes(
        {
          skip,
          limit: PAGE_LIMIT,
          ...(committedSearch ? { search: committedSearch } : {}),
          ...statusParams,
        },
        { force: isRefresh }
      );
    } catch (err) {
      console.error('Failed to load product sizes', err);
    } finally {
      if (isRefresh) setRefreshLoading(false);
    }
  }, [fetchProductSizes, currentPage, committedSearch, statusFilter]);

  React.useEffect(() => {
    loadProductSizes();
  }, [loadProductSizes]);

  /**
   * Global error watcher for product size operations
   * - Displays errors via the global error dialog when `error` is set
   */
  React.useEffect(() => {
    if (error) {
      showError(error, strings.page?.alerts?.errorTitle);
      // Clear it as soon as it has been shown. The hook holds `error` until the next
      // request replaces it, so leaving it set means any later run of this effect
      // re-opens the dialog the operator just dismissed — which reads as a frozen
      // screen, because every click reopens it.
      clearError();
    }
  }, [error, showError, clearError, strings.page]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCommittedSearch(value);
      setCurrentPage(1);
    }, 400);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
    setSelectedProductSizes([]);
  };

  /**
   * Selection helpers
   */
  const selectProductSize = (productSizeId, checked) => {
    setSelectedProductSizes(prev => {
      if (checked) {
        return [...prev, productSizeId];
      } else {
        return prev.filter(id => id !== productSizeId);
      }
    });
  };

  const selectAllProductSizes = (checked) => {
    if (checked) {
      setSelectedProductSizes(productSizes.map(ps => ps.id));
    } else {
      setSelectedProductSizes([]);
    }
  };

  const hasSelection = selectedProductSizes.length > 0;
  const selectionCount = selectedProductSizes.length;

  /** Refresh the product sizes list, forcing a server reload */
  const handleRefresh = () => {
    loadProductSizes(true);
  };

  /** Open product size form for creating a new product size */
  const handleCreateProductSize = () => {
    setEditingProductSize(null);
    setShowProductSizeForm(true);
  };

  /** Open product size form prefilled for editing the given productSizeId */
  const handleEditProductSize = async (productSizeId) => {
    setActionLoading(true);
    try {
      const result = await getProductSizeById(productSizeId);
      if (result.success && result.data) {
        setEditingProductSize(result.data);
        setShowProductSizeForm(true);
      }
    } catch (err) {
      console.error('Failed to fetch product size for editing:', err);
    } finally {
      setActionLoading(false);
    }
  };

  /** Open view modal and load product size details for the provided productSizeId */
  const handleViewProductSize = async (productSizeId) => {
    setViewLoading(true);
    setViewingProductSize(null);
    setShowViewModal(true);
    try {
      // Call the service directly with force=true to get fresh data from the API
      const result = await productSizesService.getProductSizeById(productSizeId, { force: true });
      if (result.success && result.data) {
        setViewingProductSize(result.data);
      } else {
        console.error('Failed to fetch product size:', result.error);
        showError(result.error || 'Failed to load product size details', strings.page?.alerts?.errorTitle);
      }
      console.log('View product size result:', result);
    } catch (err) {
      console.error('Failed to fetch product size for viewing:', err);
      showError(err.message || 'Failed to load product size details', strings.page?.alerts?.errorTitle);
    } finally {
      setViewLoading(false);
    }
  };

  /** Open distribution modal for the provided productSizeId */
  const handleViewDistribution = (productSizeId) => {
    setDistributionProductId(productSizeId);
    setShowDistributionModal(true);
  };

  /**
   * Prepare delete confirmation for a single product size
   * - resolves display name for the confirm message and sets confirm state
   */
  const handleDeleteProductSize = (productSizeId) => {
    const productSize = productSizes.find((ps) => ps.id === productSizeId) || null;
    const displayName = productSize?.alias_name || strings.table?.copy?.unnamedProductSize;

    setDeleteConfirm({
      type: 'single',
      productSize,
      productSizeId,
      title: strings.confirm?.deleteSingle?.title,
      message: strings.confirm?.deleteSingle?.message?.replace('{name}', displayName),
      confirmLabel: strings.confirm?.deleteSingle?.confirmLabel,
      loadingLabel: strings.confirm?.deleteSingle?.loadingLabel,
      cancelLabel: strings.confirm?.buttons?.cancel,
      confirmVariant: 'danger',
    });
  };

  /**
   * Prepare delete confirmation for bulk selected product sizes
   */
  const handleBulkDelete = () => {
    const message = selectionCount === 1
      ? strings.confirm?.deleteBulk?.messageSingle
      : strings.confirm?.deleteBulk?.messageMultiple?.replace('{count}', selectionCount);

    setDeleteConfirm({
      type: 'bulk',
      count: selectionCount,
      title: strings.confirm?.deleteBulk?.title,
      message,
      confirmLabel: strings.confirm?.deleteBulk?.confirmLabel,
      loadingLabel: strings.confirm?.deleteBulk?.loadingLabel,
      cancelLabel: strings.confirm?.buttons?.cancel,
      confirmVariant: 'danger',
    });
  };

  /** Execute the confirmed delete (single or bulk) operation */
  const confirmDelete = async () => {
    setActionLoading(true);
    try {
      let result;

      if (deleteConfirm.type === 'single') {
        const idToDelete = deleteConfirm.productSizeId || deleteConfirm.productSize?.id;
        if (!idToDelete) {
          throw new Error(t('productSizes.errors.noProductSizeIdToDelete'));
        }
        result = await deleteProductSize(idToDelete);
      } else {
        // For bulk delete, pass the selected product size IDs
        result = await bulkDeleteProductSizes(selectedProductSizes || []);
      }

      if (result.success) {
        setDeleteConfirm(null);
        setSelectedProductSizes([]); // Clear selection after successful delete
      } else {
        // Service returned an error
        const errorMsg = result.error || 'Delete operation failed';
        setDeleteConfirm(null);
        setAlertMessage({
          type: 'error',
          title: strings.confirm?.errorTitle,
          message: errorMsg,
        });
      }
    } catch (err) {
      console.error('Delete operation failed:', err);
      const errorMsg = err?.message || 'Delete operation failed';
      setDeleteConfirm(null);
      setAlertMessage({
        type: 'error',
        title: strings.confirm?.errorTitle,
        message: errorMsg,
      });
    } finally {
      setActionLoading(false);
    }
  };

  /** Handle submit from the product size form for create or update */
  const handleFormSubmit = async (productSizeData) => {
    setActionLoading(true);
    try {
      let result;

      if (editingProductSize) {
        result = await updateProductSize(editingProductSize.id, productSizeData);
      } else {
        result = await createProductSize(productSizeData);
      }

      if (result.success) {
        setShowProductSizeForm(false);
        setEditingProductSize(null);
        setSelectedProductSizes([]); // Clear selection after create/update
      }

      return result;
    } catch (err) {
      console.error('Form submission failed:', err);
      return { success: false, error: 'Operation failed' };
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Count product sizes helper
   * - Returns the server-provided totalCount or falls back to the array length
   */
  const countProductSizes = () => {
    if (typeof totalCount === 'number' && totalCount >= 0) return totalCount;
    if (Array.isArray(productSizes)) return productSizes.length;
    return 0;
  };

  const totalProductSizesCount = countProductSizes();

  /** Handle import modal open */
  const handleImportOpen = () => {
    setImportError(null);
    setImportSuccess(false);
    setShowImportModal(true);
  };

  /** Handle import modal close */
  const handleImportClose = () => {
    setShowImportModal(false);
    setImportError(null);
    setImportSuccess(false);
  };

  /** Handle import file upload and processing */
  const handleImport = async (file) => {
    setImporting(true);
    setImportError(null);
    setImportSuccess(false);

    try {
      const result = await importProductSizes(file);

      if (result.success) {
        // Close the import modal on successful import. The hook already refreshes the list and shows notifications.
        handleImportClose();
      } else {
        setImportError(result.error || 'Import failed. Please try again.');
      }
    } catch (error) {
      console.error('Import failed:', error);
      setImportError(error.message || 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <AdminGuard fallback={(
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {t('access.accessDenied.title')}
          </h2>
          <p className="text-gray-600">{t('access.accessDenied.message')}</p>
        </div>
      </div>
    )}>
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
        {/* Alert Message */}
        {alertMessage && (
          <div className="fixed top-4 right-4 z-50">
            <MessageAlert
              type={alertMessage.type}
              title={alertMessage.title}
              message={alertMessage.message}
              onClose={() => setAlertMessage(null)}
            />
          </div>
        )}

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {strings.page?.header?.title}
              </h1>
              <p className="mt-2 text-gray-600">
                {strings.page?.header?.subtitle}
              </p>
            </div>

            {/* Stats */}
            <div className="flex space-x-4">
              <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-500">
                    {strings.page?.stats?.total}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{totalProductSizesCount}</div>
                </div>
              </div>
              {hasSelection && (
                <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-blue-600">
                      {strings.page?.stats?.selected}
                    </div>
                    <div className="text-2xl font-bold text-blue-700">{selectionCount}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <ProductSizeActions
          selectedProductSizes={selectedProductSizes}
          productSizes={productSizes}
          onAddProductSize={handleCreateProductSize}
          onDeleteSelected={handleBulkDelete}
          onRefresh={handleRefresh}
          onImport={handleImportOpen}
          onExport={() => exportProductSizes({ search: committedSearch })}
          onDownloadTemplate={() => { }}
          showSearch={true}
          searchValue={searchQuery}
          onSearchChange={handleSearchChange}
          loading={actionLoading}
          refreshLoading={refreshLoading}
        />

        {/* Status filter: default shows only active sizes; deactivated
            (soft-deleted) sizes stay hidden until switched here. */}
        <div className="flex items-center gap-2 mt-4">
          <label htmlFor="ps-status-filter" className="text-sm font-medium text-gray-600">
            Status
          </label>
          <select
            id="ps-status-filter"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
            <option value="all">All</option>
          </select>
        </div>

        {/* Product Sizes Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6 overflow-hidden w-full">
          <div
            className="overflow-x-auto"
            style={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin'
            }}
          >
            <ProductSizesTable
              productSizes={productSizes}
              loading={loading}
              selectedProductSizes={selectedProductSizes}
              onSelectProductSize={selectProductSize}
              onSelectAll={selectAllProductSizes}
              onEditProductSize={handleEditProductSize}
              onDeleteProductSize={handleDeleteProductSize}
              onViewProductSize={handleViewProductSize}
              onViewDistribution={handleViewDistribution}
              viewLoading={viewLoading}
            />
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-6 py-3 mx-4 mb-4">
              <p className="text-sm text-gray-500">
                Page {currentPage} of {totalPages} &mdash; {totalCount} total
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  Previous
                </button>
                {(() => {
                  const pages = [];
                  const delta = 2;
                  const left = Math.max(2, currentPage - delta);
                  const right = Math.min(totalPages - 1, currentPage + delta);
                  pages.push(1);
                  if (left > 2) pages.push('...');
                  for (let i = left; i <= right; i++) pages.push(i);
                  if (right < totalPages - 1) pages.push('...');
                  if (totalPages > 1) pages.push(totalPages);
                  return pages.map((p, i) =>
                    p === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-gray-400">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        disabled={loading}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium border"
                        style={currentPage === p
                          ? { backgroundColor: '#0066cc', color: '#fff', borderColor: '#0066cc' }
                          : { borderColor: '#e5e7eb', color: '#374151' }
                        }
                      >
                        {p}
                      </button>
                    )
                  );
                })()}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || loading}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Product Size Form Modal */}
        {showProductSizeForm && (
          <ProductSizeForm
            productSize={editingProductSize}
            isOpen={showProductSizeForm}
            onClose={() => {
              setShowProductSizeForm(false);
              setEditingProductSize(null);
            }}
            onSubmit={handleFormSubmit}
            loading={actionLoading}
          />
        )}

        {/* Product Size Details Modal */}
        {showViewModal && (
          <ProductSizeDetails
            isOpen={showViewModal}
            onClose={() => {
              setShowViewModal(false);
              setViewingProductSize(null);
            }}
            productSize={viewingProductSize}
            loading={viewLoading}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {deleteConfirm && (
          <ConfirmDialog
            isOpen
            title={deleteConfirm.title}
            message={deleteConfirm.message}
            confirmLabel={deleteConfirm.confirmLabel}
            cancelLabel={deleteConfirm.cancelLabel}
            confirmVariant={deleteConfirm.confirmVariant}
            loading={actionLoading}
            loadingLabel={deleteConfirm.loadingLabel}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteConfirm(null)}
          />
        )}

        {/* Import Modal */}
        {showImportModal && (
          <ProductSizeImportModal
            isOpen={showImportModal}
            onClose={handleImportClose}
            onImport={handleImport}
            importing={importing}
            importError={importError}
            importSuccess={importSuccess}
          />
        )}

        {/* Distribution Modal */}
        {showDistributionModal && (
          <ProductSizeDistributionModal
            key={distributionProductId}
            isOpen={showDistributionModal}
            onClose={() => {
              setShowDistributionModal(false);
              setDistributionProductId(null);
            }}
            productSizeId={distributionProductId}
          />
        )}
      </div>
    </AdminGuard>
  );
};

export default ProductSizesPage;
